import React, { useState, useEffect } from 'react';
import { Container } from './styles';
import {
  GoSync,
  GoCheck,
  GoAlert,
  GoDownload,
  GoTrash,
  GoEye,
  GoFileDirectory,
  GoOrganization
} from 'react-icons/go';
import { HiOutlineDocumentText } from 'react-icons/hi';
import { BiData } from 'react-icons/bi';
import { FiBarChart2, FiCalendar, FiFile, FiPlay, FiShield, FiFolder, FiSearch, FiUpload } from 'react-icons/fi';
import { IoCubeOutline } from 'react-icons/io5';
import NovoRelatorioModal from './components/NovoRelatorioModal';

export default function SpedProcessorDashboard() {
  const [spedFiles, setSpedFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    totalProcessed: 0,
    totalRecords: 0,
    errors: 0,
    success: 0
  });

  // Configuration state
  const [startDate, setStartDate] = useState('01/01/2024');
  const [endDate, setEndDate] = useState('31/12/2024');
  const [startDateError, setStartDateError] = useState('');
  const [endDateError, setEndDateError] = useState('');
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [availableCompanies, setAvailableCompanies] = useState([
    { id: '1', name: 'AUTOMEC COMERCIAL DE VEICULOS LTDA', cnpj: '12.345.678/0001-90', color: '#3b82f6' },
    { id: '2', name: 'VIDAL COMERCIO LTDA', cnpj: '98.765.432/0001-10', color: '#10b981' },
    { id: '3', name: 'EMPRESA EXEMPLO LTDA', cnpj: '11.222.333/0001-44', color: '#f59e0b' },
    { id: '4', name: 'COMERCIAL ABC LTDA', cnpj: '55.666.777/0001-88', color: '#8b5cf6' },
    { id: '5', name: 'INDUSTRIA XYZ SA', cnpj: '99.888.777/0001-66', color: '#ef4444' }
  ]);
  const [showNovoRelatorioModal, setShowNovoRelatorioModal] = useState(false);

  useEffect(() => {
    fetchSpedFiles();
  }, []);

  const fetchSpedFiles = async () => {
    setLoadingFiles(true);
    try {
      // Mock data - arquivos SPED disponíveis
      setTimeout(() => {
        const mockFiles = [
          {
            id: '1',
            filename: 'SPED_FISCAL_012024.txt',
            company: 'AUTOMEC COMERCIAL',
            cnpj: '12.345.678/0001-90',
            period: '01/2024',
            size: '2.5 MB',
            uploadDate: '2024-12-10T10:30:00Z',
            status: 'pending',
            records: 15420
          },
          {
            id: '2',
            filename: 'SPED_FISCAL_022024.txt',
            company: 'AUTOMEC COMERCIAL',
            cnpj: '12.345.678/0001-90',
            period: '02/2024',
            size: '2.8 MB',
            uploadDate: '2024-12-10T10:35:00Z',
            status: 'pending',
            records: 16890
          },
          {
            id: '3',
            filename: 'SPED_CONTRIBUICOES_012024.txt',
            company: 'VIDAL COMERCIO',
            cnpj: '98.765.432/0001-10',
            period: '01/2024',
            size: '1.9 MB',
            uploadDate: '2024-12-11T14:20:00Z',
            status: 'pending',
            records: 12340
          },
          {
            id: '4',
            filename: 'SPED_FISCAL_032024.txt',
            company: 'EMPRESA EXEMPLO',
            cnpj: '11.222.333/0001-44',
            period: '03/2024',
            size: '3.2 MB',
            uploadDate: '2024-12-12T09:15:00Z',
            status: 'pending',
            records: 18750
          },
          {
            id: '5',
            filename: 'SPED_CONTRIBUICOES_022024.txt',
            company: 'VIDAL COMERCIO',
            cnpj: '98.765.432/0001-10',
            period: '02/2024',
            size: '2.1 MB',
            uploadDate: '2024-12-12T11:45:00Z',
            status: 'pending',
            records: 13560
          }
        ];
        
        setSpedFiles(mockFiles);
        setLoadingFiles(false);
      }, 500);
    } catch (error) {
      console.error('Error fetching SPED files:', error);
      setLoadingFiles(false);
    }
  };

  const handleStartProcessing = async () => {
    if (selectedFiles.length === 0) {
      alert('Selecione pelo menos um arquivo SPED');
      return;
    }

    setProcessing(true);
    
    try {
      // TODO: Implement API call
      // Processing selected SPED files
      
      setTimeout(() => {
        setProcessing(false);
        setStats({
          totalProcessed: selectedFiles.length,
          totalRecords: selectedFiles.reduce((sum, id) => {
            const file = spedFiles.find(f => f.id === id);
            return sum + (file?.records || 0);
          }, 0),
          errors: 0,
          success: selectedFiles.length
        });
        setSelectedFiles([]);
      }, 2000);
    } catch (error) {
      console.error('Error processing SPEDs:', error);
      setProcessing(false);
    }
  };

  const toggleCompany = (companyId) => {
    setSelectedCompanies(prev => 
      prev.includes(companyId)
        ? prev.filter(id => id !== companyId)
        : [...prev, companyId]
    );
  };

  const toggleFile = (fileId) => {
    setSelectedFiles(prev => 
      prev.includes(fileId)
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  };

  const selectAllFiles = () => {
    const filteredFiles = getFilteredFiles();
    const allFilteredSelected = filteredFiles.every(f => selectedFiles.includes(f.id));
    
    if (allFilteredSelected) {
      setSelectedFiles(prev => prev.filter(id => !filteredFiles.find(f => f.id === id)));
    } else {
      const newSelected = [...new Set([...selectedFiles, ...filteredFiles.map(f => f.id)])];
      setSelectedFiles(newSelected);
    }
  };

  const getFilteredFiles = () => {
    if (!searchTerm.trim()) return spedFiles;
    
    const term = searchTerm.toLowerCase();
    return spedFiles.filter(file => 
      file.filename.toLowerCase().includes(term) ||
      file.company.toLowerCase().includes(term) ||
      file.cnpj.includes(term) ||
      file.period.includes(term)
    );
  };

  const handleUploadSped = () => {
    setShowNovoRelatorioModal(true);
  };

  const handleModalComplete = () => {
    // Recarregar lista de arquivos após upload
    fetchSpedFiles();
  };

  // Formatar data automaticamente (DD/MM/AAAA)
  const formatDate = (value) => {
    const numbers = value.replace(/\D/g, '');
    const limited = numbers.slice(0, 8);
    
    if (limited.length <= 2) {
      return limited;
    } else if (limited.length <= 4) {
      return `${limited.slice(0, 2)}/${limited.slice(2)}`;
    } else {
      return `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4)}`;
    }
  };

  // Validar se a data é válida
  const isValidDate = (dateString) => {
    if (dateString.length !== 10) return false;
    
    const [day, month, year] = dateString.split('/').map(Number);
    
    if (!day || !month || !year) return false;
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;
    if (year < 1900 || year > 2100) return false;
    
    const daysInMonth = new Date(year, month, 0).getDate();
    if (day > daysInMonth) return false;
    
    return true;
  };

  // Converter string de data para objeto Date
  const parseDate = (dateString) => {
    const [day, month, year] = dateString.split('/').map(Number);
    return new Date(year, month - 1, day);
  };

  // Validar data inicial
  const validateStartDate = (date) => {
    if (!date) {
      setStartDateError('Data inicial é obrigatória');
      return false;
    }
    
    if (date.length === 10 && !isValidDate(date)) {
      setStartDateError('Data inválida');
      return false;
    }
    
    if (date.length === 10 && endDate.length === 10 && isValidDate(date) && isValidDate(endDate)) {
      const start = parseDate(date);
      const end = parseDate(endDate);
      
      if (start > end) {
        setStartDateError('Data inicial não pode ser maior que a final');
        return false;
      }
    }
    
    setStartDateError('');
    return true;
  };

  // Validar data final
  const validateEndDate = (date) => {
    if (!date) {
      setEndDateError('Data final é obrigatória');
      return false;
    }
    
    if (date.length === 10 && !isValidDate(date)) {
      setEndDateError('Data inválida');
      return false;
    }
    
    if (date.length === 10 && startDate.length === 10 && isValidDate(date) && isValidDate(startDate)) {
      const start = parseDate(startDate);
      const end = parseDate(date);
      
      if (end < start) {
        setEndDateError('Data final não pode ser menor que a inicial');
        return false;
      }
    }
    
    setEndDateError('');
    return true;
  };

  const handleStartDateChange = (e) => {
    const formatted = formatDate(e.target.value);
    setStartDate(formatted);
    
    if (formatted.length === 10) {
      validateStartDate(formatted);
    } else {
      setStartDateError('');
    }
  };

  const handleEndDateChange = (e) => {
    const formatted = formatDate(e.target.value);
    setEndDate(formatted);
    
    if (formatted.length === 10) {
      validateEndDate(formatted);
    } else {
      setEndDateError('');
    }
  };

  // Selecionar todo o texto ao clicar
  const handleDateClick = (e) => {
    e.target.select();
  };

  // Revalidar quando uma data muda
  useEffect(() => {
    if (startDate.length === 10) {
      validateStartDate(startDate);
    }
  }, [endDate]);

  useEffect(() => {
    if (endDate.length === 10) {
      validateEndDate(endDate);
    }
  }, [startDate]);

  const formatDateDisplay = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatNumber = (num) => {
    return num.toLocaleString('pt-BR');
  };

  return (
    <Container>
      {/* Header */}
      <div className="header-info">
        <div className="header-icon">
          <FiPlay />
        </div>
        <div className="header-details">
          <h3>Processar SPED's - Importação e Processamento</h3>
          <div className="header-meta">
            <span className="update-tag">Atualizado agora</span>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="kpi-cards main-stats">
        <div className="kpi-card primary">
          <div className="card-content">
            <div className="thumb">
              <FiFile />
            </div>
            <div className="text">
              <p>{stats.totalProcessed}</p>
              <p>Total Processados</p>
            </div>
          </div>
        </div>

        <div className="kpi-card info">
          <div className="card-content">
            <div className="thumb">
              <BiData />
            </div>
            <div className="text">
              <p>{formatNumber(stats.totalRecords)}</p>
              <p>Total Registros</p>
            </div>
          </div>
        </div>

        <div className="kpi-card warning">
          <div className="card-content">
            <div className="thumb">
              <GoAlert />
            </div>
            <div className="text">
              <p>{formatNumber(stats.errors)}</p>
              <p>Erros</p>
            </div>
          </div>
        </div>

        <div className="kpi-card success">
          <div className="card-content">
            <div className="thumb">
              <GoCheck />
            </div>
            <div className="text">
              <p>{formatNumber(stats.success)}</p>
              <p>Sucesso</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Split Layout */}
      <div className="split-layout">
        {/* Left Side - Configuration Panel */}
        <div className="config-section">
          <div className="section-header">
            <h4><FiBarChart2 /> Configurar Processamento</h4>
          </div>

          <div className="config-panel">
            {/* Date Range */}
            <div className="config-group">
              <label>Período</label>
              <div className="date-inputs">
                <div className="date-field">
                  <FiCalendar />
                  <input
                    type="text"
                    placeholder="DD/MM/AAAA"
                    value={startDate}
                    onChange={handleStartDateChange}
                    onClick={handleDateClick}
                    maxLength={10}
                    className={startDateError ? 'error' : ''}
                  />
                </div>
                <span className="separator">até</span>
                <div className="date-field">
                  <FiCalendar />
                  <input
                    type="text"
                    placeholder="DD/MM/AAAA"
                    value={endDate}
                    onChange={handleEndDateChange}
                    onClick={handleDateClick}
                    maxLength={10}
                    className={endDateError ? 'error' : ''}
                  />
                </div>
              </div>
              {(startDateError || endDateError) && (
                <div className="date-error">
                  <GoAlert />
                  <span>{startDateError || endDateError}</span>
                </div>
              )}
            </div>

            {/* Company Selector */}
            <div className="config-group">
              <label>
                <GoOrganization /> Empresas
                <span className="count">({selectedCompanies.length} selecionada{selectedCompanies.length !== 1 ? 's' : ''})</span>
              </label>
              <div className="company-list">
                {availableCompanies.map(company => (
                  <div
                    key={company.id}
                    className={`company-item ${selectedCompanies.includes(company.id) ? 'selected' : ''}`}
                    onClick={() => toggleCompany(company.id)}
                  >
                    <div className="company-cube" style={{ color: company.color }}>
                      <IoCubeOutline />
                    </div>
                    <div className="company-info">
                      <span className="company-name">{company.name}</span>
                      <span className="company-cnpj">{company.cnpj}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Start Button */}
            <button
              className="start-btn"
              onClick={handleStartProcessing}
              disabled={processing || selectedFiles.length === 0}
            >
              {processing ? (
                <>
                  <GoSync className="spin" />
                  Processando...
                </>
              ) : (
                <>
                  <FiPlay />
                  Processar Arquivos ({selectedFiles.length})
                </>
              )}
            </button>
          </div>
        </div>

        {/* Right Side - SPED Files List */}
        <div className="files-section">
          <div className="section-header">
            <h4><FiFolder /> Arquivos SPED Disponíveis ({getFilteredFiles().length})</h4>
          </div>

          {/* Toolbar com busca e botões */}
          <div className="files-toolbar">
            <div className="search-box">
              <FiSearch className="search-icon" />
              <input
                type="text"
                placeholder="Buscar por nome, empresa, CNPJ ou período..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button 
              className="toolbar-btn upload"
              onClick={handleUploadSped}
            >
              <FiUpload />
              Enviar SPED's
            </button>
            <button 
              className="toolbar-btn select-all"
              onClick={selectAllFiles}
            >
              {getFilteredFiles().every(f => selectedFiles.includes(f.id)) && getFilteredFiles().length > 0 
                ? 'Desmarcar Todos' 
                : 'Selecionar Todos'}
            </button>
          </div>

          <div className="files-list">
            {loadingFiles ? (
              <div className="empty-state">
                <GoSync className="spin" />
                <p>Carregando arquivos...</p>
              </div>
            ) : spedFiles.length === 0 ? (
              <div className="empty-state">
                <GoFileDirectory />
                <p>Nenhum arquivo SPED disponível</p>
                <span>Faça upload de arquivos SPED para começar</span>
              </div>
            ) : getFilteredFiles().length === 0 ? (
              <div className="empty-state">
                <FiSearch />
                <p>Nenhum arquivo encontrado</p>
                <span>Tente ajustar os termos de busca</span>
              </div>
            ) : (
              getFilteredFiles().map((file) => (
                <div 
                  key={file.id} 
                  className={`file-card ${selectedFiles.includes(file.id) ? 'selected' : ''}`}
                  onClick={() => toggleFile(file.id)}
                >
                  <div className="file-header">
                    <div className="file-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedFiles.includes(file.id)}
                        onChange={() => toggleFile(file.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="file-icon">
                      <HiOutlineDocumentText />
                    </div>
                    <div className="file-title">
                      <span className="filename">{file.filename}</span>
                      <span className={`status-badge ${file.status}`}>
                        {file.status === 'pending' ? 'Pendente' : 'Processado'}
                      </span>
                    </div>
                  </div>

                  <div className="file-meta">
                    <span className="company">
                      <GoOrganization />
                      {file.company}
                    </span>
                    <span className="cnpj">{file.cnpj}</span>
                  </div>

                  <div className="file-info">
                    <div className="info-item">
                      <FiCalendar />
                      <span>Período: {file.period}</span>
                    </div>
                    <div className="info-item">
                      <FiFile />
                      <span>{file.size}</span>
                    </div>
                    <div className="info-item">
                      <BiData />
                      <span>{formatNumber(file.records)} registros</span>
                    </div>
                  </div>

                  <div className="file-footer">
                    <span className="upload-date">
                      Upload: {formatDateDisplay(file.uploadDate)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Modal Novo Relatório */}
      <NovoRelatorioModal
        isOpen={showNovoRelatorioModal}
        onClose={() => setShowNovoRelatorioModal(false)}
        onComplete={handleModalComplete}
      />
    </Container>
  );
}
